import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import api from '../../../lib/api';
import toast from 'react-hot-toast';
import { formatForInput } from '../../../lib/dateUtils';

const EMPTY_FORM = {
  name: '',
  siteCode: '',
  address: '',
  city: '',
  state: '',
  postcode: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  notes: '',
  testingDueDate: '',
};

/**
 * Form state, CRUD mutations, and submit handlers for the Sites page.
 * Composed by useSites — not imported by page components directly.
 */
export function useSitesFormActions(queryClient) {
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [showMasterDateConfirm, setShowMasterDateConfirm] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const { register, watch, setValue } = useForm({ defaultValues: { notes: '' } });

  const resetForm = () => {
    setShowForm(false);
    setEditingSite(null);
    setShowMasterDateConfirm(false);
    setPendingSubmit(null);
    setIsSubmitting(false);
    setValue('notes', '');
    setFormData({ ...EMPTY_FORM });
  };

  const createMutation = useMutation(
    (data) => api.post('/sites', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sites');
        toast.success('Site created');
        resetForm();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create site'),
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/sites/${id}`, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('sites');
        toast.success(data?.message || 'Site updated');
        resetForm();
      },
      onError: (err) => {
        const msg = err.response?.data?.error ?? err.response?.data?.message ?? err.message ?? 'Failed to update site';
        toast.error(msg);
        console.error('Site update failed:', err);
      },
      onSettled: () => {
        setIsSubmitting(false);
        setShowMasterDateConfirm(false);
        setPendingSubmit(null);
      },
    }
  );

  useMutation(
    (id) => api.delete(`/sites/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sites');
        toast.success('Site deleted');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete site'),
    }
  );

  const openEdit = (site) => {
    setEditingSite(site);
    setValue('notes', site.notes || '');
    setFormData({
      name: site.name || '',
      siteCode: site.site_code || '',
      address: site.address || '',
      city: site.city || '',
      state: site.state || '',
      postcode: site.postcode || '',
      contactName: site.contact_name || '',
      contactPhone: site.contact_phone || '',
      contactEmail: site.contact_email || '',
      notes: site.notes || '',
      testingDueDate: site.testing_due_date ? formatForInput(site.testing_due_date) : '',
    });
    setShowForm(true);
  };

  const buildSitePayload = () => {
    const rawDate = formData.testingDueDate?.trim();
    const testingDueDate = rawDate ? formatForInput(rawDate) : undefined;
    const notesVal = (watch('notes') || '').trim() || undefined;
    return {
      name: formData.name || undefined,
      siteCode: formData.siteCode || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      postcode: formData.postcode || undefined,
      contactName: formData.contactName || undefined,
      contactPhone: formData.contactPhone || undefined,
      contactEmail: formData.contactEmail || undefined,
      notes: notesVal,
      testingDueDate: testingDueDate || undefined,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = buildSitePayload();
    if (editingSite) {
      const prevDate = editingSite.testing_due_date ? formatForInput(editingSite.testing_due_date) : '';
      const newDate = payload.testingDueDate || '';
      if (prevDate !== newDate && (prevDate || newDate)) {
        setPendingSubmit({ id: editingSite.id, data: payload });
        setShowMasterDateConfirm(true);
        return;
      }
      setIsSubmitting(true);
      updateMutation.mutate({ id: editingSite.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const confirmMasterDateChange = () => {
    if (pendingSubmit) {
      setIsSubmitting(true);
      updateMutation.mutate(pendingSubmit);
      setShowMasterDateConfirm(false);
      setPendingSubmit(null);
    }
  };

  return {
    showForm,
    setShowForm,
    editingSite,
    showMasterDateConfirm,
    setShowMasterDateConfirm,
    setPendingSubmit,
    isSubmitting,
    formData,
    setFormData,
    register,
    resetForm,
    openEdit,
    handleSubmit,
    confirmMasterDateChange,
  };
}
